import React, { useState, useEffect } from 'react';
import './CocktailFlashcards.css';

const CocktailFlashcards = ({ onBack }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [wrongCards, setWrongCards] = useState([]);
  const [studyMode, setStudyMode] = useState('normal'); // 'normal' or 'review'
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [studyStats, setStudyStats] = useState({
    totalStudied: 0,
    correctAnswers: 0,
    sessionStartTime: Date.now()
  });
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    loadCocktailFlashcards();
  }, []);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim()) {
      const results = [];
      flashcards.forEach(category => {
        category.cards.forEach(card => {
          if (
            card.front.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.back.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.tips.toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            results.push({ ...card, category: category.category });
          }
        });
      });
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, flashcards]);

  // Keyboard event handling for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showDetails) {
        setShowDetails(false);
      }
    };

    if (showDetails) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDetails]);

  const loadCocktailFlashcards = async () => {
    try {
      const response = await fetch('/cocktail-flashcards.json');
      const data = await response.json();
      setFlashcards(data);
      setCategories(data.map(cat => cat.category));
      setLoading(false);
    } catch (error) {
      console.error('Error loading cocktail flashcards:', error);
      setLoading(false);
    }
  };

  const selectCategory = (category) => {
    setCurrentCategory(category);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setShowDetails(false);
    setStudyMode('normal');
  };

  const getCurrentCards = () => {
    if (!currentCategory) return [];
    
    if (studyMode === 'review') {
      return wrongCards;
    }
    
    const categoryData = flashcards.find(cat => cat.category === currentCategory);
    let cards = categoryData ? categoryData.cards : [];
    
    // Apply search filter
    if (searchTerm) {
      cards = cards.filter(card => 
        card.front.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.back.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.tips.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply difficulty filter
    if (difficultyFilter !== 'all') {
      cards = cards.filter(card => card.difficulty === difficultyFilter);
    }
    
    return cards;
  };

  const currentCards = getCurrentCards();
  const currentCard = currentCards[currentCardIndex];

  const nextCard = () => {
    if (currentCardIndex < currentCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
      setShowDetails(false);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
      setShowDetails(false);
    }
  };

  const markAsWrong = () => {
    if (currentCard && studyMode === 'normal') {
      const cardWithCategory = { ...currentCard, category: currentCategory };
      setWrongCards(prev => {
        const exists = prev.some(card => 
          card.front === currentCard.front && card.category === currentCategory
        );
        if (!exists) {
          return [...prev, cardWithCategory];
        }
        return prev;
      });
    }
    
    // Update stats
    setStudyStats(prev => ({
      ...prev,
      totalStudied: prev.totalStudied + 1
    }));
    
    nextCard();
  };

  const markAsCorrect = () => {
    if (studyMode === 'review' && currentCard) {
      setWrongCards(prev => prev.filter((card, index) => index !== currentCardIndex));
      if (currentCardIndex >= wrongCards.length - 1) {
        setCurrentCardIndex(Math.max(0, wrongCards.length - 2));
      }
    } else {
      nextCard();
    }
    
    // Update stats
    setStudyStats(prev => ({
      ...prev,
      totalStudied: prev.totalStudied + 1,
      correctAnswers: prev.correctAnswers + 1
    }));
  };

  const startReviewMode = () => {
    if (wrongCards.length > 0) {
      setStudyMode('review');
      setCurrentCardIndex(0);
      setIsFlipped(false);
      setShowDetails(false);
    }
  };

  const resetStudy = () => {
    setCurrentCategory(null);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setShowDetails(false);
    setWrongCards([]);
    setStudyMode('normal');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '#28a745';
      case 'Medium': return '#ffc107';
      case 'Hard': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Gin Cocktails': return '🌿';
      case 'Vodka Cocktails': return '❄️';
      case 'Tequila & Mezcal': return '🌵';
      case 'Whiskey & Bourbon': return '🥃';
      case 'Rum Cocktails': return '🏝️';
      case 'Brandy & Cognac': return '🍇';
      case 'Scotch Whisky': return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
      case 'Wine & Champagne': return '🥂';
      default: return '🍹';
    }
  };

  if (loading) {
    return (
      <div className="cocktail-flashcard-container">
        <div className="loading">Loading cocktail flashcards...</div>
      </div>
    );
  }

  if (!currentCategory) {
    return (
      <div className="cocktail-flashcard-container">
        <div className="cocktail-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Menu
          </button>
          <h2>🍸 Cocktail Flashcards</h2>
          <p>Master classic cocktail recipes and techniques</p>
          
          {/* Study Stats */}
          {studyStats.totalStudied > 0 && (
            <div className="study-stats">
              <span>Session: {studyStats.correctAnswers}/{studyStats.totalStudied} correct</span>
              <span>Accuracy: {Math.round((studyStats.correctAnswers / studyStats.totalStudied) * 100)}%</span>
            </div>
          )}
        </div>

        {/* Search and Filter Controls */}
        <div className="search-filters">
          <input
            type="text"
            placeholder="Search cocktails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="difficulty-filter"
          >
            <option value="all">All Difficulties</option>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
            <option value="Expert">Expert</option>
          </select>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results-section">
            <h3>🔍 Search Results ({searchResults.length} found)</h3>
            <div className="search-results-grid">
              {searchResults.map((cocktail, index) => (
                <div 
                  key={`${cocktail.category}-${index}`}
                  className={`search-result-card`}
                >
                  <div className="result-header">
                    <h4>{cocktail.front}</h4>
                    <span className="result-category">{cocktail.category}</span>
                    <div 
                      className="difficulty-badge small"
                      style={{ backgroundColor: getDifficultyColor(cocktail.difficulty) }}
                    >
                      {cocktail.difficulty}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {searchTerm && searchResults.length === 0 && (
          <div className="no-results">
            <p>No cocktails found for "{searchTerm}"</p>
          </div>
        )}

        <div className="category-grid">
          {categories.map((category) => {
            const categoryData = flashcards.find(cat => cat.category === category);
            const cardCount = categoryData ? categoryData.cards.length : 0;
            
            return (
              <div 
                key={category}
                className="category-card"
                onClick={() => selectCategory(category)}
              >
                <div className="category-icon">
                  {getCategoryIcon(category)}
                </div>
                <h3>{category}</h3>
                <p>{cardCount} cocktails</p>
                <div className="category-preview">
                  {categoryData && categoryData.cards.slice(0, 3).map((card, index) => (
                    <div 
                      key={index}
                      className="difficulty-dot"
                      style={{ backgroundColor: getDifficultyColor(card.difficulty) }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {wrongCards.length > 0 && (
          <div className="review-section">
            <h3>🔄 Review Mode</h3>
            <p>You have {wrongCards.length} cocktails to review</p>
            <button className="review-button" onClick={startReviewMode}>
              Review Missed Cocktails
            </button>
          </div>
        )}
      </div>
    );
  }

  if (currentCards.length === 0) {
    return (
      <div className="cocktail-flashcard-container">
        <div className="cocktail-header">
          <button className="back-button" onClick={() => setCurrentCategory(null)}>
            ← Back to Categories
          </button>
          <h2>No cocktails available</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="cocktail-flashcard-container">
      <div className="cocktail-header">
        <button className="back-button" onClick={() => setCurrentCategory(null)}>
          ← Back to Categories
        </button>
        <h2>
          {studyMode === 'review' ? '🔄 Review Mode' : `${getCategoryIcon(currentCategory)} ${currentCategory}`}
        </h2>
        <div className="progress-info">
          Cocktail {currentCardIndex + 1} of {currentCards.length}
          {studyMode === 'normal' && wrongCards.length > 0 && (
            <span className="wrong-count"> • {wrongCards.length} to review</span>
          )}
        </div>
      </div>

      <div className="cocktail-study">
        <div className={`cocktail-flashcard ${isFlipped ? 'flipped' : ''}`}>
          <div className="flashcard-inner">
            <div className="flashcard-front">
              <div className="card-content">
                <h3>{currentCard.front}</h3>
                <div className="flip-hint">Click to reveal recipe</div>
              </div>
              <div 
                className="difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(currentCard.difficulty) }}
              >
                {currentCard.difficulty}
              </div>
            </div>
            <div className="flashcard-back">
              <div className="card-content">
                <h3>Recipe:</h3>
                <p className="recipe">{currentCard.back}</p>
                
                <button 
                  className="details-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDetails(!showDetails);
                  }}
                >
                  {showDetails ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
              <div 
                className="difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(currentCard.difficulty) }}
              >
                {currentCard.difficulty}
              </div>
            </div>
          </div>
        </div>

        {/* Modal overlay for cocktail details */}
        {showDetails && (
          <div 
            className="cocktail-details-overlay" 
            onClick={() => setShowDetails(false)}
          >
            <div 
              className="cocktail-details-modal" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>{currentCard.front} - Details</h3>
                <button 
                  className="close-button"
                  onClick={() => setShowDetails(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-content">
                <div className="detail-row">
                  <strong>Recipe:</strong> {currentCard.back}
                </div>
                <div className="detail-row">
                  <strong>Method:</strong> {currentCard.method}
                </div>
                <div className="detail-row">
                  <strong>Glassware:</strong> {currentCard.glassware}
                </div>
                <div className="detail-row">
                  <strong>Garnish:</strong> {currentCard.garnish}
                </div>
                <div className="detail-row tips">
                  <strong>Pro Tips:</strong> {currentCard.tips}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="cocktail-controls">
          <button 
            className="nav-button prev"
            onClick={prevCard}
            disabled={currentCardIndex === 0}
          >
            ← Previous
          </button>

          <button 
            className="flip-button"
            onClick={() => setIsFlipped(!isFlipped)}
          >
            {isFlipped ? 'Show Name' : 'Show Recipe'}
          </button>

          <button 
            className="nav-button next"
            onClick={nextCard}
            disabled={currentCardIndex === currentCards.length - 1}
          >
            Next →
          </button>
        </div>

        {isFlipped && (
          <div className="answer-feedback">
            <p>Did you know the recipe?</p>
            <div className="feedback-buttons">
              <button className="wrong-button" onClick={markAsWrong}>
                ❌ Need to Practice
              </button>
              <button className="correct-button" onClick={markAsCorrect}>
                ✅ Got it Right
              </button>
            </div>
          </div>
        )}

        <div className="study-actions">
          <button className="reset-button" onClick={resetStudy}>
            🔄 Reset Study Session
          </button>
          {wrongCards.length > 0 && studyMode === 'normal' && (
            <button className="review-button" onClick={startReviewMode}>
              📝 Review {wrongCards.length} Missed Cocktails
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CocktailFlashcards; 